import {connect} from "react-redux"
import Button from "material-ui/FlatButton"
import RefreshIcon from "material-ui/svg-icons/navigation/refresh"
import React from "react"
import compose from "recompose/compose"
import withProps from "recompose/withProps"
import lifecycle from "recompose/lifecycle"
import withPropsOnChange from "recompose/withPropsOnChange"
import {createSelector, createStructuredSelector} from "reselect"
import {url as swUrl} from "../registerServiceWorker"
import * as Util from "../common"
import {assert} from "@swarm2/assert"
import {containers} from "@swarm2/core"

// how long before we window.location.reload() if the user ignores the button
const FORCE_UPDATE_AFTER_MILLIS = 300 * 1000
// show a countdown shortly before forcing an update, so the user isn't surprised
const COUNTDOWN_AT_MILLIS = FORCE_UPDATE_AFTER_MILLIS - 120 * 1000
// How often we'll check the server for updates. There's an automatic update-check
// on load, but people often leave swarmsim tabs open for a very long time.
const POLL_UPDATE_MILLIS = 20 * 60 * 1000

const sinceClientDeployed = (s, p) => p.sinceClientDeployed
const untilReloadMillis = createSelector(
  sinceClientDeployed,
  since =>
    since === undefined ? undefined : FORCE_UPDATE_AFTER_MILLIS - since,
)
const isReloadReady = createSelector(
  untilReloadMillis,
  until => (until === undefined ? undefined : until <= 0),
)
const untilCountdownMillis = createSelector(
  sinceClientDeployed,
  since => (since === undefined ? undefined : COUNTDOWN_AT_MILLIS - since),
)
const isCountdownReady = createSelector(
  untilCountdownMillis,
  until => (until === undefined ? undefined : until <= 0),
)

const HOT_RELOAD_ENABLED =
  module.hot &&
  assert(
    process.env.NODE_ENV !== "production",
    "Hot-reloading in production???",
  )

const container = HOT_RELOAD_ENABLED
  ? C => console.warn("UpdateButton disabled: hot reloading works here") || C
  : compose(
      containers.updateWatcher.UpdateWatcher,
      // Lots of stuff here is www-specific and can't fit in core or mobile, sadly.
      withProps({
        reload() {
          window.location.reload()
        },
      }),
      lifecycle({
        componentWillMount() {
          //window.setTimeout(setClientDeployedAt, 2000)
          // Careful not to reload instantly - remember that bug from swarm1, where
          // the service worker didn't have time to update itself?
          //
          // I'll leave logging in even in production, because service workers are
          // otherwise pretty silent, not unit testable, ugly when things go
          // wrong, and notoriously difficult to debug.
          if (
            process.env.NODE_ENV === "production" &&
            "serviceWorker" in navigator
          ) {
            console.log("attempting service worker registration")
            window.addEventListener("load", () => {
              navigator.serviceWorker
                .register(swUrl)
                .then(registration => {
                  console.log("service worker registered")
                  registration.onupdatefound = () => {
                    console.log("client update found")
                    const installingWorker = registration.installing
                    installingWorker.onstatechange = () => {
                      console.log(
                        "client update state changed",
                        installingWorker.state,
                      )
                      if (installingWorker.state === "installed") {
                        if (navigator.serviceWorker.controller) {
                          this.props.setClientDeployedAt(this.props.now)
                          console.info(
                            "client updated; please refresh.",
                            this.props,
                          )
                        } else {
                          this.setState({offlineCacheSetAt: this.props.now})
                          console.info(
                            "client cached for offline use.",
                            this.props,
                          )
                        }
                      }
                    }
                  }
                  const checkUpdate = source => () => {
                    console.log("checking for client update:", source)
                    registration.update()
                  }
                  this.setState({checkUpdate})
                  // Poll for updates periodically.
                  window.setInterval(
                    checkUpdate("periodic"),
                    POLL_UPDATE_MILLIS,
                  )
                  // Check for updates on load.
                  checkUpdate("initial")()
                  // And one more way, a bit below: we watch serverDeployedAt for updates.
                  // Firebase server and client deployment is atomic, but server version
                  // update is a separate step afterward.
                })
                .catch(error => {
                  console.error(
                    "service worker registration attempted but failed",
                    error,
                  )
                })
            })
          } else {
            //console.log(
            //  "service workers not supported. I'll refresh when the server's updated, hoping that there's a corresponding client update.",
            //)
            const checkUpdate = source => () => {
              if (!this.props.serverDeployedAt) return
              //console.log(
              //  "someone called checkClientUpdate with service workers disabled. " +
              //    "I'll trust that there's a client update, since we only do server update " +
              //    "checks without service workers.",
              //  source,
              //)
              this.props.setClientDeployedAt(this.props.now)
            }
            this.setState({checkUpdate})
          }
        },
        componentWillUnmount() {
          console.warn(
            "UpdateWatcher unmounted. That's not good (but not disastrous).",
          )
        },
      }),
      // In addition to periodic polling, check for updates when the server's updated.
      // The "should I update" function is pretty complex - don't update on load
      // (serverDeployedAt from null to notnull), but on a real change (notnull to notnull).
      withPropsOnChange(
        (oldProps, newProps) => {
          return (
            newProps.checkUpdate &&
            oldProps.serverDeployedAt &&
            newProps.serverDeployedAt &&
            oldProps.serverDeployedAt !== newProps.serverDeployedAt &&
            (console.log(
              "server updated",
              oldProps.serverDeployedAt,
              newProps.serverDeployedAt,
            ) ||
              true)
          )
        },
        // withPropsOnChange runs on load too, but I don't want this to do so, hence
        // the empty-check. Only on change.
        props =>
          props.checkUpdate &&
          props.serverDeployedAt &&
          props.checkUpdate("server update")(),
      ),
      // We're done detecting updates; these are for the countdown timer until forced-reload.
      connect(
        createStructuredSelector({
          untilReloadMillis,
          isReloadReady,
          isCountdownReady,
        }),
      ),
      withPropsOnChange(["isReloadReady"], props => {
        if (props.isReloadReady) {
          return props.reload()
        }
      }),
    )

const UpdateButtonRender = function({
  untilReloadMillis,
  isClientDeployed,
  isCountdownReady,
  isReloadReady,
  reload,
  clientVersion,
}) {
  //if (!isClientDeployed) return <small>v{clientVersion}</small>
  if (!isClientDeployed) return null
  return (
    <Button
      icon={<RefreshIcon />}
      label={
        isReloadReady ? (
          <span>Reloading, one moment...</span>
        ) : isCountdownReady ? (
          <span>
            Updating in <Util.Duration millis={untilReloadMillis} />...
          </span>
        ) : (
          <span>New version ready</span>
        )
      }
      onClick={reload}
    />
  )
}

export default container(UpdateButtonRender)



// WEBPACK FOOTER //
// ./src/updates/UpdateButton.js